import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Shield, Brain, Target, CheckCircle, ArrowRight } from 'lucide-react';
import { services } from '../mock';

const Services = () => {
  const serviceIcons = {
    'ERP Mega Projects': Target,
    'Cybersecurity Assurance': Shield,
    'AI Adoption & Data Assurance': Brain,
    'IT Governance & Oversight': Eye
  };

  return (
    <div className="page-container">
      {/* Services Header */}
      <section className="services-header">
        <h1 className="display-huge">Our Services</h1>
        <p className="body-large services-subtitle">
          Independent oversight and expert guidance for your most critical technology initiatives
        </p>
      </section>

      {/* Services Grid */}
      <section className="services-detailed-section">
        {services.map((service, index) => {
          const IconComponent = serviceIcons[service.title] || Target;
          return (
            <div key={index} className="service-detailed-card">
              <div className="service-detailed-header">
                <div className="service-icon-large">
                  <IconComponent size={48} />
                </div>
                <h2 className="display-medium">{service.title}</h2>
              </div>
              
              <p className="body-large service-detailed-description">
                {service.description}
              </p>

              <div className="service-detailed-content">
                <h3 className="heading-3">What We Deliver:</h3>
                <ul className="service-detailed-list">
                  {service.details.map((detail, idx) => (
                    <li key={idx} className="body-medium">
                      <CheckCircle size={20} />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {index === 0 && (
                <div className="service-highlight">
                  <h4 className="heading-3">Why ERP Projects Fail:</h4>
                  <p className="body-medium">
                    Most ERP transformations fail due to over-customization, vendor-driven agendas, 
                    weak governance, unrealistic timelines, and cultural resistance. We address these 
                    root causes to ensure your program stays disciplined, aligned, and sustainable.
                  </p>
                </div>
              )}

              {index === 1 && (
                <div className="service-highlight">
                  <h4 className="heading-3">Beyond Compliance:</h4>
                  <p className="body-medium">
                    We don't just help you check boxes. Our approach builds genuine cyber resilience 
                    that protects your organization from evolving threats while maintaining board-level 
                    accountability and control.
                  </p>
                </div>
              )}

              {index === 2 && (
                <div className="service-highlight">
                  <h4 className="heading-3">AI with Guardrails:</h4>
                  <p className="body-medium">
                    Innovation without risk management is reckless. We help you adopt AI technologies 
                    with proper data protection, ethical guidelines, and measurable ROI—ensuring 
                    responsible innovation that drives real business value.
                  </p>
                </div>
              )}

              {index === 3 && (
                <div className="service-highlight">
                  <h4 className="heading-3">Executive Control:</h4>
                  <p className="body-medium">
                    Outsourcing and consulting relationships can drift without proper oversight. 
                    We act as your voice of dissent, ensuring vendors and consultants remain 
                    accountable to your strategic goals and board-level standards.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* Our Approach Section */}
      <section className="approach-section">
        <h2 className="display-large">Our Approach</h2>
        <div className="approach-grid">
          <div className="approach-card">
            <h3 className="heading-2">Independent Perspective</h3>
            <p className="body-medium">
              Free from vendor bias, we provide impartial advice that prioritizes your 
              organization's best interests—not sales quotas or implementation hours.
            </p>
          </div>
          <div className="approach-card">
            <h3 className="heading-2">Board-Level Oversight</h3>
            <p className="body-medium">
              We work directly with boards and executive management to provide the clarity 
              and accountability needed for confident decision-making.
            </p>
          </div>
          <div className="approach-card">
            <h3 className="heading-2">Voice of Dissent</h3>
            <p className="body-medium">
              When hard decisions need to be made, we provide the dissenting perspective 
              that helps leadership see risks and alternatives clearly.
            </p>
          </div>
          <div className="approach-card">
            <h3 className="heading-2">Proven Track Record</h3>
            <p className="body-medium">
              20+ years leading complex transformations, from $200M ERP migrations to 
              post-breach cyber recovery, with measurable results and lasting impact.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2 className="display-large">Ready to Transform with Confidence?</h2>
        <p className="body-large">
          Let's discuss how independent oversight can reduce risk and drive success in your 
          digital transformation initiatives.
        </p>
        <Link to="/contact" className="btn-primary">
          Schedule a Consultation
          <ArrowRight size={20} />
        </Link>
      </section>
    </div>
  );
};

export default Services;
